import { Component, OnInit, Input, TemplateRef, Output, EventEmitter } from '@angular/core';
import UserLevelEnum = Boo.Objects.Enums.UserLevelEnum;
import CustomerUser = Boo.Objects.CustomerUser;
import { CustomerUserService } from '../../../services/customer-user.service';
import { UserService } from '../../../services/user.service';
import { catchError, filter, finalize, switchMap } from 'rxjs/operators';
import { SessionStorageService } from '../../../services/session-storage.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomerUserComponent } from '../CustomerUser/CustomerUser.component';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { ConfirmBox } from '../../shared/ConfirmBox/confirm-box';

@Component({
  selector: 'app-components-managecustomer-customerusers',
  templateUrl: './CustomerUsers.component.html'
})
export class CustomerUsersComponent implements OnInit {
  @Input() customer: Boo.Objects.Customer;
  @Input() user: Boo.Objects.User;
  @Input() partner: Boo.Objects.Partner;
  @Input() footerTemplate: TemplateRef<any>;
  @Output() refreshCustomer = new EventEmitter<void>();
  isLoading = false;
  countryAbbreviation: string;
  users: CustomerUser[];
  contactUsers: CustomerUser[];
  dashboardUsers: CustomerUser[];

  get noContactInfo(): boolean {
    return !this.users.some(x => x.CanContact);
  }

  constructor(
    private customerUserService: CustomerUserService,
    private userService: UserService,
    private sessionStorageService: SessionStorageService,
    private modalService: NgbModal,
    private confirmBox: ConfirmBox) { }

  ngOnInit(): void {
    this.isLoading = true;

    if (this.partner.PartnerId !== Boo.Objects.Enums.PartnerEnum.Boostability) {
      this.users = _.filter(this.customer.Users, (user) => {
        return user.UserLevelId !== UserLevelEnum.Specialist && !user.IsReportingUser;
      });
    } else {
      this.users = _.filter(this.customer.Users, (user) => {
        return (user.UserLevelId === UserLevelEnum.Analytics || user.UserId === this.user.UserId) && !user.IsReportingUser;
      });
    }

    this.setUserTypes();

    forkJoin([
      this.sessionStorageService.getStaticData(),
      this.customerUserService.getByIds(this.users.map(u => u.UserId))
    ]).pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: ([data, customerUsers]) => {
          const customerCountry = data.Countries.find((c: any) => c.CountryId === this.customer.CountryId);
          this.countryAbbreviation = customerCountry ? customerCountry.Abbreviation : 'US';
          this.users.forEach(u => {
            u.CustomerUsers = customerUsers.filter((cu: any) => cu.UserId === u.UserId);
            u.EmailRecipientType = customerUsers.find((cu: any) => cu.CustomerUserId === u.CustomerUserId)?.EmailRecipientType;
          });
        },
        error: error => toastr.error(error)
      });
  }

  addUser(): void {
    const newUser = {
      IsPrimary: !this.contactUsers.some(u => u.IsPrimary),
      CanContact: true,
      CanReceiveTextMessages: false,
      CustomerId: this.customer.CustomerId,
      CustomerUserId: 0,
      CustomerUserPhoneNumbers: [],
      EmailRecipientType: !this.contactUsers.some(u => u.IsPrimary) ? Boo.Objects.Enums.EmailRecipientTypes.To : null,
      InsertedDate: new Date(),
      IsCustomerUserActive: true,
      UserLevelId: UserLevelEnum.Analytics,
      PrimaryPhoneNumber: '',
      FullName: '',
      UserId: 0,
      Username: '',
      Password: '',
      FirstName: '',
      LastName: '',
      PhoneExtension: '',
      IsActive: true,
      AccessToken: '',
      CustomerUsers: [],
      PartnerUsers: [],
      Customers: [],
      Languages: [],
      Partners: [],
      UserPermissions: [],
      IsReportingUser: false
    } as CustomerUser;

    this.openUserModal(newUser);
  }

  editUser(customerUser: CustomerUser): void {
    this.openUserModal(customerUser);
  }

  deleteUser(customerUser: CustomerUser): void {
    if (customerUser.CustomerUserId === 0) {
      this.users = this.users.filter(u => u !== customerUser);
      return;
    }

    this.confirmBox.confirm('Are you sure you want to delete this user? This action cannot be undone.', { title: 'Delete User' })
      .pipe(
        filter(confirmed => confirmed),
        switchMap(() => {
          this.isLoading = true;
          return this.customerUserService.delete(customerUser.CustomerUserId);
        }),
        finalize(() => this.isLoading = false)
      ).subscribe(() => {
          toastr.success('Successfully deleted user.');

          this.refreshCustomer.emit();
          this.users = this.users.filter(u => u !== customerUser);
          this.setUserTypes();
      });
  }

  makePrimaryContact(customerUser: CustomerUser) {
    if (customerUser.IsPrimary) {
      toastr.info('This user is already the primary contact.');
      return;
    }

    if (!customerUser.PrimaryPhoneNumber?.trim()) {
      toastr.warning('A phone number is required for the primary contact. Edit the contact to add one.');
      return;
    }

    this.isLoading = true;
    const currentPrimaryContact = this.users.find(u => u.IsPrimary);
    const previousRecipientType = customerUser.EmailRecipientType;

    customerUser.IsPrimary = true;
    customerUser.EmailRecipientType = Boo.Objects.Enums.EmailRecipientTypes.To;

    this.saveCustomerUser(customerUser)
      .pipe(
        catchError(error => {
          customerUser.IsPrimary = false;
          customerUser.EmailRecipientType = previousRecipientType;
          return throwError(() => error);
        }),
        switchMap(() => currentPrimaryContact ? this.demotePrimaryContact(currentPrimaryContact) : of(null)),
        finalize(() => this.isLoading = false)
      ).subscribe({
        next: () => {
          toastr.success('Primary contact updated.');

          this.refreshCustomer.emit();
          this.setUserTypes();
        },
        error: (error) => {
          toastr.error(error);

          this.refreshCustomer.emit();
          this.setUserTypes();
        },
      });
  }

  makeDashboardOnly(customerUser: CustomerUser): void {
    if (customerUser.IsPrimary) {
      toastr.warning('Cannot change the customer\'s primary contact to reporting dashboard access.');
      return;
    }

    this.isLoading = true;
    customerUser.CanContact = false;
    this.saveCustomerUser(customerUser)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: () => {
          toastr.success('Contact changed to reporting dashboard access.');

          this.refreshCustomer.emit();
        },
        error: (error) => {
          toastr.error(error);

          this.refreshCustomer.emit();
        }
      });
  }

  private openUserModal(customerUser: CustomerUser): void {
    const modalRef = this.modalService.open(CustomerUserComponent, { size: 'lg' });

    modalRef.componentInstance.customer = this.customer;
    modalRef.componentInstance.customerUser = customerUser;
    modalRef.componentInstance.partner = this.partner;
    modalRef.componentInstance.countryAbbreviation = this.countryAbbreviation;
    modalRef.componentInstance.refreshCustomer = this.refreshCustomer;

    modalRef.result.then(
      (newCustomerUser: CustomerUser) => {
        this.refreshCustomer.emit();

        if (newCustomerUser) {
          this.users.push(newCustomerUser);
          this.setUserTypes();
        }
      },
      () => { /* no action needed on dismiss */ }
    );
  }

  private setUserTypes(): void {
    this.contactUsers = this.users.sort(u => u.IsPrimary ? -1 : 1).filter(u => u.CanContact);
    this.dashboardUsers = this.users.filter(u => !u.CanContact);
  }

  private demotePrimaryContact(customerUser: CustomerUser): Observable<any> {
    customerUser.IsPrimary = false;

    return this.saveCustomerUser(customerUser)
      .pipe(catchError(error => {
        customerUser.IsPrimary = true;
        return throwError(() => error);
      }));
  }

  private saveCustomerUser(customerUser: CustomerUser): Observable<any> {
    return this.userService.save(
      customerUser,
      customerUser.CustomerId,
      customerUser.IsCustomerUserActive,
      customerUser.IsPrimary,
      customerUser.CanContact,
      customerUser.CanReceiveTextMessages,
      customerUser.CustomerUserPhoneNumbers,
      customerUser.EmailRecipientType
    );
  }
}
